import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { FadeIn } from "@/components/Motion";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imgOk, setImgOk] = useState(true);

  useSeo({
    title: post ? `${post.title} | Varun Marbles` : undefined,
    description: post?.meta_description || undefined,
    path: `/blog/${slug}`,
  });

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    setImgOk(true);
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="max-w-3xl mx-auto px-6 pt-40 pb-40 text-center" data-testid="blog-not-found">
        <h1 className="font-serif text-4xl font-light tracking-tight mb-6">Story not found.</h1>
        <Link to="/blog" className="text-xs tracking-[0.2em] uppercase border-b border-foreground/40 pb-1">
          Back to the journal
        </Link>
      </section>
    );
  }

  if (!post) return <div className="min-h-screen" />;

  return (
    <article className="max-w-3xl mx-auto px-6 pt-32 md:pt-52 pb-28 md:pb-40" data-testid="blog-post-page">
      <FadeIn>
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-secondary-foreground mb-12"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
          The Blog
        </Link>
        <div className="flex flex-wrap items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-secondary-foreground mb-6">
          <span>{formatDate(post.published_at)}</span>
          {post.tags?.map((t) => (
            <span key={t} className="border border-border px-2.5 py-1">{t}</span>
          ))}
        </div>
        <h1 data-testid="blog-post-title" className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] mb-8">
          {post.title}
        </h1>
        {post.image_url && imgOk && (
          <div className="overflow-hidden aspect-[16/9] mb-12">
            <img
              src={post.image_url}
              alt={post.title}
              onError={() => setImgOk(false)}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div
          data-testid="blog-post-content"
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: post.content_html }}
        />
      </FadeIn>
    </article>
  );
}
